import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'lib/httpClient';
import trpcApi from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';

type UseFundFullyDeployedParams = {
  fundId: string;
  viaAllocationPreview?: boolean;
};

export function useFundFullyDeployed(params: UseFundFullyDeployedParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const utils = trpcApi.useUtils();

  const fullyDeployedLog = useQuery({
    queryKey: ['fund-fully-deployed-logs', params.fundId],
    queryFn: async () => {
      const { data: response } = await api.get(
        `fund/fully-deployed-logs/${params.fundId}`,
      );
      return response?.data;
    },
    enabled: !!params.fundId,
  });

  const fullyDeployFund = useMutation({
    mutationFn: async () => {
      const { data: response } = await api.post(
        `fund/fully-deploy/${params.fundId}`,
      );
      return response;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fund-fully-deployed-logs'],
      });
      utils.eisAllocations.getAllocation.invalidate();
    },
  });

  const undoFundDeployment = useMutation({
    mutationFn: async () => {
      const { data: response } = await api.post(
        `fund/undo-fund-deployment/${params.fundId}`,
        {
          viaAllocationPreview: params.viaAllocationPreview ?? false,
        },
      );
      return response;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['fund-fully-deployed-logs'],
      });
      utils.eisAllocations.getAllocation.invalidate();
      notification.success('Tranche undeployed successfully.');
    },
  });

  return {
    fullyDeployedLog,
    fullyDeployFund,
    undoFundDeployment,
  };
}
